import axios from 'axios';

import apiConfig from '@/config/api';
import http from '@/services/http';

const ACCOUNT_KEY = 'accounts';

function parseAccounts() {
  try {
    const raw = localStorage.getItem(ACCOUNT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveAccounts(accounts) {
  localStorage.setItem(ACCOUNT_KEY, JSON.stringify(accounts));
}

function normalizeRole(data) {
  const roleId = Number(data?.profile?.role_id);
  const serverType = Number(data?.server?.type);

  if (roleId === 1) return 'superadmin';
  if (roleId === 4) return 'director';
  if (roleId === 2) return 'parent';
  if (roleId === 3) return 'child';
  if (serverType === 3) return 'oo';
  if (serverType === 4) return 'uo';

  return undefined;
}

function resolveAvatar(roleId, sex) {
  const role = Number(roleId) === 2 ? 'p' : 's';
  const gender = Number(sex) === 2 ? '_f' : '_m';
  return {
    url: `/static/img/${role}${gender}.png`,
    style: `url(/static/img/${role}${gender}.png)`
  };
}

function persistSession(authData) {
  if (!authData?.profile) {
    throw { status: 6, message: 'Profile not found' };
  }
  if (!authData?.server) {
    throw { status: 3, message: 'Server not found' };
  }
  if (!authData?.user) {
    throw { status: 7, message: 'User not found' };
  }

  const role = normalizeRole(authData);
  if (!role) {
    throw { status: 4, message: 'Role not found' };
  }

  const token = authData?.token || authData?._auth?.token || authData?.body?.token || '';
  const serverId = authData?.server?.id || authData?._auth?.server?.id || authData?.body?.server?.id || '';

  localStorage.setItem('token', token);
  localStorage.setItem('institution-id', `${serverId}`);
  localStorage.setItem('currentRole', role);
  localStorage.setItem('person', JSON.stringify(authData));

  const children = authData?.profile?.children;
  if (!children && Number(authData?.profile?.role_id) === 2) {
    throw { status: 1, message: 'Children not found' };
  }

  const accounts = parseAccounts();
  const login = authData?.user?.login;
  if (login && !accounts.some((item) => item?.profile?.user?.login === login)) {
    accounts.push({
      profile: authData,
      avatar: resolveAvatar(authData?.profile?.role_id, authData?.profile?.sex)
    });
    saveAccounts(accounts);
  }

  return { _auth: authData };
}

async function loadChildrenIfNeeded(authData) {
  const children = authData?.profile?.children;
  if (!Array.isArray(children) || children.length === 0) {
    return;
  }

  const params = { expand: 'server', 'per-page': 20 };
  children.forEach((uin, index) => {
    params[`uin[${index}]`] = uin;
  });

  try {
    const response = await http.get('/profiles/children', { params });
    localStorage.setItem('childList', JSON.stringify(response.data || []));
  } catch {
    localStorage.removeItem('childList');
  }
}

export async function login(payload) {
  try {
    const response = await axios.post(apiConfig.USER.LOGIN, { ...payload, app: 1 });
    const authData = response?.data;
    const result = persistSession(authData);
    await loadChildrenIfNeeded(authData);
    return result;
  } catch (error) {
    if (error?.status) {
      throw error;
    }
    if (error?.response?.status) {
      throw { status: error.response.status, body: error.response.data };
    }
    throw { status: 500, body: error };
  }
}

export async function loginWithAccount(accountProfile) {
  const result = persistSession(accountProfile);
  await loadChildrenIfNeeded(accountProfile);
  return result;
}

export function getAccounts() {
  return parseAccounts();
}

export function removeAccount(index) {
  const accounts = parseAccounts();
  if (index >= 0 && index < accounts.length) {
    accounts.splice(index, 1);
    saveAccounts(accounts);
  }
  return accounts;
}

export async function recoverPassword(loginValue, language) {
  const headerLang = language === 'kz' ? 'kk-KZ' : 'ru-RU';
  return axios.post(
    apiConfig.USER.RECOVER,
    { login: loginValue, token: 'N$83By%HqmzpoCfO2DnGdFm' },
    { headers: { 'x-lang': headerLang } }
  );
}

export async function sendConfirmationEmail(email) {
  return axios.post(apiConfig.USER.SEND_MESSAGE_MAIL, { email });
}

export async function updateProfileEmail(id, email) {
  return axios.put(`${apiConfig.USER.SINGLE}/${id}`, { email });
}

export async function acceptAgreement(userId) {
  return axios.put(`${apiConfig.USER.ACCEPT_AGREEMENT}/${userId}`, { is_agree_license: 1 });
}
